import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { authAPI } from '@/api/new-api'

export type SaltoFobMarker = 'internal' | 'external'

export type SaltoFob = {
  clientId: number
  fobId: string | null
  fobHolderId: string
  fobMarker: SaltoFobMarker | null
  fobHolderName: string | null
  expirationDate: string | null
  doorIds: string[]
  permissionGroupIds: string[]
  accessLevelIds: string[]
}

export type SaltoFobAccess = {
  doorIds: string[]
  permissionGroupIds: string[]
  accessLevelIds: string[]
  expirationDate?: string
}

export type UpdateSaltoFobPayload = SaltoFobAccess & {
  fobHolderId: string
  encoderId: string
}

export const getSaltoSpaceFobsQueryKey = (buildingId: number, userEmail: string) => [
  'useGetSaltoSpaceFobs',
  buildingId,
  userEmail,
]

const getSaltoSpaceFobs = async (buildingId: number, userEmail: string): Promise<SaltoFob[]> => {
  try {
    const response = await authAPI.get<SaltoFob[]>(`/salto-space/fob/${buildingId}`, {
      params: { userEmail },
    })
    return response.data ?? []
  } catch (error) {
    if ((error as { response?: { status?: number } })?.response?.status === 404) return []
    throw error
  }
}

export const useGetSaltoSpaceFobs = (buildingId: number, userEmail: string, enabled = true) =>
  useQuery({
    enabled: enabled && Boolean(buildingId) && Boolean(userEmail),
    queryFn: () => getSaltoSpaceFobs(buildingId, userEmail),
    queryKey: getSaltoSpaceFobsQueryKey(buildingId, userEmail),
  })

export const useUpdateSaltoSpaceFob = (buildingId: number, userEmail: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: UpdateSaltoFobPayload) =>
      authAPI.post('/salto-space/update-fob', { buildingId, userEmail, ...payload }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: getSaltoSpaceFobsQueryKey(buildingId, userEmail) }),
  })
}

export const useRevokeSaltoSpaceFob = (buildingId: number, userEmail: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: { fobHolderId: string; encoderId: string }) =>
      authAPI.post('/salto-space/revoke-fob', { buildingId, userEmail, ...payload }),
    onSuccess: (_response, payload) =>
      queryClient.setQueryData<SaltoFob[]>(
        getSaltoSpaceFobsQueryKey(buildingId, userEmail),
        (fobs) => (fobs ?? []).filter((fob) => fob.fobHolderId !== payload.fobHolderId),
      ),
  })
}

export const useDeleteSaltoSpaceFob = (buildingId: number, userEmail: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: { fobHolderId: string }) =>
      authAPI.post('/salto-space/ban-key', { buildingId, userEmail, ...payload }),
    onSuccess: (_response, payload) =>
      queryClient.setQueryData<SaltoFob[]>(
        getSaltoSpaceFobsQueryKey(buildingId, userEmail),
        (fobs) => (fobs ?? []).filter((fob) => fob.fobHolderId !== payload.fobHolderId),
      ),
  })
}
